import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useWebData } from "../../Context/webDataContext";
import Navbar from "../navbar/Navbar";
import Footer from "../footer/Footer";
import LoadingScreen from "../../utils/LoadingScreen";
import parse from "html-react-parser";

const Error = () => (
  <div className="w-full h-screen flex items-center justify-center">
    <LoadingScreen />
  </div>
);

function BlogDetailDynamic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getBlogData } = useWebData();
  const blogs = getBlogData();
  const currentIndex = blogs.findIndex((blog) => blog.id === id);

  // On initial mount, optionally scroll to top smoothly
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  if (currentIndex === -1) return <Error />;

  const blog = blogs[currentIndex];

  // Sort blogs by blogReleaseTimestamp (descending order)
  const sortedBlogs = [...blogs].sort(
    (a, b) => new Date(b.blogReleaseTimestamp) - new Date(a.blogReleaseTimestamp)
  );
  const mostViewed = sortedBlogs.slice(0, 5);

  // Navigation logic: determine the next and previous blog
  const getNextBlog = () =>
    currentIndex + 1 < blogs.length ? blogs[currentIndex + 1] : blogs[0];
  const getPrevBlog = () =>
    currentIndex - 1 >= 0 ? blogs[currentIndex - 1] : blogs[blogs.length - 1];

  // Handle next and prev clicks by first smoothly scrolling up,
  // then navigating after a short delay.
  const handleNext = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setTimeout(() => {
      navigate(`/blog/${getNextBlog().id}`);
    }, 600);
  };

  const handlePrev = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setTimeout(() => {
      navigate(`/blog/${getPrevBlog().id}`);
    }, 600);
  };

  // Compute release date and time
  const releaseDate = blog.blogReleaseTimestamp
    ? new Date(blog.blogReleaseTimestamp)
    : null;
  const displayDate = releaseDate
    ? releaseDate.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";
  const displayTime = releaseDate
    ? releaseDate.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <>
      <Navbar />
      <div className="w-full px-4 md:px-12 lg:px-20 md:py-10 pt-24 relative">
        {/* Blog Header */}
        <section className="flex flex-col items-center text-center mb-10 space-y-4">
          <p className="uppercase text-base font-light text-gray-500">
            {blog.type}
          </p>
          <h1 className="text-2xl md:text-3xl xl:text-4xl font-bold text-gray-900 uppercase">
            {blog.blogHeader}
          </h1>
          <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <span>{displayDate}</span>
            <span className="w-1 h-1 rounded-full bg-gray-500"></span>
            <span>{displayTime}</span>
          </span>
        </section>

        {/* Blog Image */}
        <div className="w-full mb-4">
          <img
            src={blog.blogImage}
            alt={blog.blogHeader}
            className="w-full h-64 md:h-96 object-cover rounded-md shadow-lg"
          />
        </div>

        {/* Author Section */}
        <div className="flex items-center justify-between border-b pb-4 my-8">
          <div className="flex items-center gap-4">
            <img
              src={blog.blogAuthorImage}
              alt={blog.blogAuthor}
              className="w-16 h-16 rounded-lg object-cover shadow-md"
            />
            <div>
              <p className="text-lg font-semibold text-gray-800">
                {blog.blogAuthor}
              </p>
              <p className="text-sm text-gray-600">{blog.type}</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 uppercase font-medium">
            {blog.readingTime} reading time
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          {/* Blog Content */}
          <div className="blog-content mb-2 space-y-6 w-full lg:w-2/3 rich-content">
            {Array.isArray(blog.blogDetailTextContent) ? (
              blog.blogDetailTextContent.map((section, idx) => (
                <div key={idx} className="mb-8">
                  <div className="text-gray-700 text-lg leading-relaxed">
                    {parse(section.paragraph)}
                  </div>
                  {section.imageUrl && (
                    <img
                      src={section.imageUrl}
                      alt={section.header || `Section ${idx + 1}`}
                      className="w-full h-64 object-cover my-4 rounded-2xl shadow-md"
                    />
                  )}
                  <div className="text-gray-600">
                    {parse(section.description)}
                  </div>
                </div>
              ))
            ) : (
              <div className="mb-8 text-gray-700 text-lg leading-relaxed">
                {parse(blog.blogDetailTextContent)}
              </div>
            )}
          </div>

          {/* Most Viewed Section */}
          <div className="most-viewed hidden lg:block w-1/3">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b pb-2">
              Most Viewed Stories
            </h2>
            <ul className="space-y-4 mb-4">
              {mostViewed.map((mv, index) => (
                <li
                  key={mv.id}
                  className="flex items-start gap-4 p-3 border rounded-lg shadow-sm hover:shadow-md transition "
                >
                  <span className="text-lg font-semibold text-gray-800">
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <Link
                      to={`/blog/${mv.id}`}
                      className="primary-text text-sm font-semibold hover:text-amber-700"
                    >
                      {mv.blogHeader}
                    </Link>
                    <p className="text-gray-600 text-xs mt-1">
                      {mv.paragraph.slice(0, 50)}...
                    </p>
                  </div>
                  <img
                    src={mv.blogImage}
                    alt={mv.blogHeader}
                    className="w-16 h-auto object-cover rounded-md"
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Next & Previous Buttons */}
        <div className="flex justify-between mb-5">
          <button
            onClick={handlePrev}
            className="flex items-center gap-2 px-5 py-2 primary-bg text-white rounded-lg shadow-md hover:bg-green-700 transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Previous
          </button>
          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-5 py-2 primary-bg text-white rounded-lg shadow-md hover:bg-green-700 transition"
          >
            Next
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default BlogDetailDynamic;
